/**
 * Maps wheel **cadence** to readout steps: `round(refMs / max(5, dt))` steps per event, clamped.
 * Shorter `dt` between same‑direction notches ⇒ more ±3 steps (faster zoom); long gaps ⇒ 1 step.
 */
const CTRL_WHEEL_MOUSE_VELOCITY_REF_MS = 38

const CTRL_WHEEL_MOUSE_VELOCITY_MAX_STEPS_PER_EVENT = 14

/** Gaps longer than this (ms) reset acceleration (treat as a new gesture at 1 step). */
const CTRL_WHEEL_MOUSE_VELOCITY_IDLE_GAP_MS = 220

/**
 * Legacy `wheelDelta` / `wheelDeltaY` (still present on Chromium) usually moves in steps of ±120 per
 * “line”; high‑res wheels use ±40. Below this, pixel-only heuristics apply (trackpad stays under 48px).
 */
const CTRL_WHEEL_LEGACY_LINE_DELTA_ABS_MIN = 40

/** Chrome mouse often sends ~53px per detent with no usable legacy delta; trackpad stays smaller. */
const CTRL_WHEEL_MOUSE_PIXEL_DELTA_ABS_MIN = 48

/** Above this, treat as a single large wheel impulse (still one notch unit per event). */
const CTRL_WHEEL_MOUSE_PIXEL_DELTA_ABS_CAP = 200

function isCtrlWheelMousePixelNotchWheel(e: WheelEvent): boolean {
  if (e.deltaMode !== WheelEvent.DOM_DELTA_PIXEL) return false

  const anyE = e as WheelEvent & { wheelDelta?: number; wheelDeltaY?: number }
  const legacy = anyE.wheelDeltaY ?? anyE.wheelDelta

  if (
    typeof legacy === 'number' &&
    legacy !== 0 &&
    Math.abs(legacy) >= CTRL_WHEEL_LEGACY_LINE_DELTA_ABS_MIN
  ) {
    return true
  }

  const dy = Math.abs(e.deltaY)

  return (
    (dy >= CTRL_WHEEL_MOUSE_PIXEL_DELTA_ABS_MIN && dy <= CTRL_WHEEL_MOUSE_PIXEL_DELTA_ABS_CAP) ||
    dy > CTRL_WHEEL_MOUSE_PIXEL_DELTA_ABS_CAP
  )
}

export function isCtrlWheelMouseNotchWheel(e: WheelEvent): boolean {
  if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) return true
  if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) return true

  return isCtrlWheelMousePixelNotchWheel(e)
}

/** How many ±3 readout steps one mouse wheel event should apply from scroll cadence (same direction). */
export function ctrlWheelMouseReadoutStepsForEvent(
  sig: number,
  prevT: number,
  prevS: number,
  now: number
): number {
  if (sig === 0) return 0

  if (prevS !== 0 && sig !== prevS) {
    return 1
  }

  if (prevT === 0) {
    return 1
  }

  const dt = now - prevT

  if (dt > CTRL_WHEEL_MOUSE_VELOCITY_IDLE_GAP_MS) {
    return 1
  }

  const scaled = CTRL_WHEEL_MOUSE_VELOCITY_REF_MS / Math.max(5, dt)

  return Math.min(CTRL_WHEEL_MOUSE_VELOCITY_MAX_STEPS_PER_EVENT, Math.max(1, Math.round(scaled)))
}
